import { API_BASE_URL } from "../constants/app";

export function isSpeechSupported(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

let activeAudio: HTMLAudioElement | null = null;

function getVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    const existing = window.speechSynthesis.getVoices();
    if (existing.length > 0) {
      resolve(existing);
      return;
    }
    const handleVoicesChanged = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", handleVoicesChanged);
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", handleVoicesChanged);
    // Some browsers never fire voiceschanged if voices were already cached; don't hang forever.
    setTimeout(() => resolve(window.speechSynthesis.getVoices()), 500);
  });
}

// The Web Speech API exposes no gender field on SpeechSynthesisVoice, so the
// only way to prefer a male voice is to match known male voice names across
// platforms (macOS, Windows, Chrome/Android TTS engines).
const MALE_VOICE_HINTS = [
  "male",
  "david",
  "alex",
  "daniel",
  "fred",
  "george",
  "mark",
  "thomas",
  "oliver",
  "aaron",
  "arthur",
  "gordon",
  "eric",
  "guy",
  "rishi",
  "ravi",
];

async function findVoice(lang: string): Promise<SpeechSynthesisVoice | undefined> {
  const voices = await getVoices();
  const langPrefix = lang.split("-")[0].toLowerCase();
  const matches = voices.filter(
    (voice) => voice.lang.toLowerCase() === lang.toLowerCase() || voice.lang.toLowerCase().startsWith(langPrefix)
  );
  if (matches.length === 0) return undefined;

  const maleMatch = matches.find((voice) => MALE_VOICE_HINTS.some((hint) => voice.name.toLowerCase().includes(hint)));
  return maleMatch || matches[0];
}

// Most OSes/browsers ship no Tamil speech voice, so speechSynthesis silently
// falls back to a default (English) voice. Split into <=190 char chunks and
// stream real Tamil audio from our backend's TTS proxy instead (Google's
// translate_tts endpoint 404s on requests whose Referer isn't
// translate.google.com, which is exactly what a direct browser call sends).
function chunkText(text: string, maxLength = 190): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const chunks: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxLength && current) {
      chunks.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) chunks.push(current);
  return chunks.length > 0 ? chunks : [text];
}

function speakViaRemoteTts(text: string, langCode: string, onEnd?: () => void): void {
  const chunks = chunkText(text);
  let index = 0;

  const playNext = () => {
    if (index >= chunks.length) {
      activeAudio = null;
      onEnd?.();
      return;
    }
    const chunk = chunks[index++];
    const base = API_BASE_URL?.replace(/\/$/, "") || "http://localhost:8080/api";
    const url = `${base}/tts?q=${encodeURIComponent(chunk)}&tl=${langCode}`;
    const audio = new Audio(url);
    activeAudio = audio;
    audio.onended = playNext;
    audio.onerror = () => {
      activeAudio = null;
      onEnd?.();
    };
    audio.play().catch(() => {
      activeAudio = null;
      onEnd?.();
    });
  };

  playNext();
}

export async function speakText(text: string, lang: string, onEnd?: () => void): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) return;

  cancelSpeech();

  const langCode = lang.split("-")[0].toLowerCase();
  const voice = isSpeechSupported() ? await findVoice(lang) : undefined;

  if (!voice) {
    // No matching system voice (common for Tamil) — use remote TTS audio instead.
    speakViaRemoteTts(trimmed, langCode, onEnd);
    return;
  }

  const utterance = new SpeechSynthesisUtterance(trimmed);
  utterance.lang = lang;
  utterance.voice = voice;
  utterance.onend = () => onEnd?.();
  utterance.onerror = () => onEnd?.();

  window.speechSynthesis.speak(utterance);
}

export function cancelSpeech(): void {
  if (isSpeechSupported()) {
    window.speechSynthesis.cancel();
  }
  if (activeAudio) {
    activeAudio.pause();
    activeAudio.currentTime = 0;
    activeAudio = null;
  }
}
